import { Injectable } from '@angular/core';
import { HotTableRegisterer } from '@handsontable/angular';
import Handsontable from 'handsontable';
import { BehaviorSubject } from 'rxjs';
import { Common } from 'src/app/core/constants/common';
import { FormData } from 'src/app/shared/models/form-data.model';
import { isUndefined } from 'util';
import { Message } from '../class/message';
import { HandsontableFieldTypeContrants } from '../constants/handsontable-field-types';
import { CommonService } from './common.service';
import { ConvertCurrencyService } from './convert-currency.service';
import { CreateContractService } from './create-contract.service';
import { NegotiationRenderControllerService } from './negotiation-render-controller.service';
import { buildGroupNameFormula } from './handsontable-group-formula';

const CONTRACT_CODE_TYPE = 'contract_code';
const COLUMN_FIELD = [
    {
        header: 'STT',
        fieldName: 'orderNo',
        readonly: true
    },
    {
        header: 'Tên',
        fieldName: 'name',
        readOnly: true
    },
    {
        header: 'Giá trị',
        fieldName: 'value',
        readOnly: false
    },
    {
        header: 'Ghi chú',
        fieldName: 'note',
        readOnly: true
    },
    {
        header: 'Bên nhập',
        fieldName: 'signingCustomer',
        readOnly: true
    }
];

const DATA_TYPE_SELECT = 'select';
const CUSTOMER_CODE_CATEGORIES = 'customer';
const FIELD_TYPE_COMBOBOX = 'combobox';
const FIXED_FUNCTION_MONEY_TO_TEXT = 'NUMBER_TO_MONEY_TEXT';
const FIXED_FUNCTION_NUMBER_TO_TEXT = 'NUMBER_TO_TEXT';
const FIXED_FUNCTION_SUM = 'SUM';

@Injectable()
export class NegotiationHandsontableService {

    dirty: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    hotRegister: HotTableRegisterer = new HotTableRegisterer();

    listRowInvalid: number[] = [];
    // Set only by the negotiation popup's scoped instance; other screens keep existing behavior.
    public negotiationFieldAccess: ((field: any) => boolean) = null;


    constructor(
        public renderControllerService: NegotiationRenderControllerService,
        public createContractService: CreateContractService,
        public commonService: CommonService,
        public message: Message,
        public convertCurrencyService: ConvertCurrencyService
    ) {
    }


    convertTableSettings(
        settingsDefault: Handsontable.GridSettings,
        tableSetting: FormData
    ): { settings: Handsontable.GridSettings, hiddenRows: number[] } {
        // Keep the saved five-column schema; assignments are hidden and never validated.
        tableSetting = { ...tableSetting, colHeaders: COLUMN_FIELD.map(field => field.header) };
        const modifiedSettings = {
            ...settingsDefault,
            minCols: COLUMN_FIELD.length,
            maxCols: COLUMN_FIELD.length,
            minSpareCols: 0
        };
        modifiedSettings.hiddenColumns = { columns: [4], indicators: false };
        modifiedSettings.colHeaders = tableSetting.colHeaders.map(header => {
            const headerField = COLUMN_FIELD.find(field => field.header === header);
            if (headerField) {
                return this.message.getMessage('handsontable.title.' + headerField.fieldName);
            }
            return header;
        });
        modifiedSettings.columns = [];
        const hiddenRows = [];

        for (const header of tableSetting.colHeaders) {
            const headerField = COLUMN_FIELD.find(field => field.header === header);
            const fieldName = headerField ? headerField.fieldName : null;
            modifiedSettings.columns.push({
                data: fieldName,
                readOnly: headerField ? headerField.readOnly : false
            });
        }

        if (tableSetting && tableSetting.rows) {
            tableSetting.rows.forEach((row, index) => {
                const hidden = this.negotiationFieldAccess ? !this.isNegotiationRowVisible(row)
                    : row.field && row.field.fieldConfig && row.field.fieldConfig.hide;
                if (hidden) {
                    hiddenRows.push(index);
                }
            });
        }

        if (this.negotiationFieldAccess) {
            modifiedSettings.hiddenRows = { rows: hiddenRows, indicators: false };
        }


        modifiedSettings.data = this.mapDataToCell(tableSetting);

        modifiedSettings.cells = this.convertCellRendering(tableSetting);

        modifiedSettings.afterChange = this.afterChange(tableSetting);

        modifiedSettings.beforeChange = this.beforeChange(tableSetting);

        modifiedSettings.afterValidate = (isValid, value, row, prop, source) => {
            if (prop !== 'value') {
                return;
            }
            const rowData = tableSetting.rows[row] ? tableSetting.rows[row] : null;
            if (this.negotiationFieldAccess && !this.negotiationFieldAccess(rowData && rowData.field)) {
                return true;
            }
            const cellValue = rowData && rowData.cells && rowData.cells.value ? rowData.cells.value : null;
            const instance = this.hotRegister.getInstance('negotiation-template');
            if (!isValid && !this.listRowInvalid.includes(row)) {
                if (cellValue && cellValue.type && cellValue.type.code === HandsontableFieldTypeContrants.combobox && !value) {
                    if (this.isRequiredField(rowData) && !this.hasCustomerEditor(rowData)) {
                        const errorMess = this.message.getMessage("handsontable.error.required");
                        instance.setDataAtCell(row, 3, errorMess);
                    } else if (!value) {
                        return true;
                    }
                }
                this.listRowInvalid.push(row);
            }
            if (isValid && this.listRowInvalid.includes(row)) {
                if (cellValue && cellValue.type && cellValue.type.code === HandsontableFieldTypeContrants.combobox) {
                    if (instance.getDataAtCell(row, 3)) {
                        instance.setDataAtCell(row, 3, '');
                    }
                }
                const index = this.listRowInvalid.indexOf(row);
                this.listRowInvalid.splice(index, 1);
            }

            if (!this.negotiationFieldAccess && !isValid && cellValue && cellValue.type && cellValue.type.code === HandsontableFieldTypeContrants.combobox && rowData.field && rowData.field.fieldConfig && rowData.field.fieldConfig.hide) {
                return true;
            }
        };

        modifiedSettings.beforePaste = this.beforePaste(tableSetting);

        modifiedSettings.afterRenderer = (TD, row, column, prop, value, cellProperties) => {
            if (TD.className.includes('date')) {
                const arrow = TD.getElementsByClassName('htAutocompleteArrow');
                if (arrow.length) {
                    arrow[0].setAttribute('title', this.message.getMessage('enterprise_contract.tooltips.pick_date'))
                }
            }
        }

        return {
            settings: modifiedSettings,
            hiddenRows: hiddenRows
        };
    }

    private isNegotiationRowVisible(row: any): boolean {
        // Group headings have no customer assignment of their own; retain them for visible child fields.
        return !!(row && (this.negotiationFieldAccess(row.field) ||
            (row.children || []).some(child => this.isNegotiationRowVisible(child))));
    }

    private mapDataToCell(data: FormData, customRows?: any[], indexGroup?: number): { orderNo: any, name: any, value: any, note: any }[] {
        const dataTable = [];
        let rows = data && data.rows ? data.rows : [];
        if (customRows) {
            rows = rows.slice(indexGroup);
        }
        for (const [index, row] of rows.entries()) {
            const modifiedRow = {};
            for (const headerField of COLUMN_FIELD) {
                if (!row || !row.cells) {
                    return;
                }
                const headerFieldName = headerField.fieldName;

                if (
                    row.cells[headerFieldName] &&
                    headerFieldName === 'name' &&
                    row.children
                ) {
                    modifiedRow[headerFieldName] = buildGroupNameFormula(
                        row.cells[headerFieldName].value, row.orderNo, (indexGroup || 0) + index
                    );
                    continue;
                }
                const cell = row.cells[headerFieldName] ? row.cells[headerFieldName] : null;
                // for update version handsontable
                // && (!cell.fixedFunction || cell.fixedFunction.code !== FIXED_FUNCTION_SUM)
                if (
                    !cell ||
                    (!cell.value && cell.value !== 0 &&
                        !cell.formula)
                ) {
                    modifiedRow[headerFieldName] = '';
                    continue;
                }
                const isChild = row.cells && row.cells.name && row.cells.name.className === 'child' ? true : false;
                if (cell.formula) {
                    modifiedRow[headerFieldName] = this.parseIndexFormula(cell.formula, data, isChild);
                    // for update version handsontable
                    // } else if (cell.fixedFunction && cell.fixedFunction.code === FIXED_FUNCTION_SUM) {
                    //     modifiedRow[headerFieldName] = this.parseSumFunction(cell, data);
                } else {
                    modifiedRow[headerFieldName] = cell.value || cell.value === 0 ? cell.value : '';
                }
            }
            dataTable.push(modifiedRow);
        }

        return dataTable;
    }

    private convertCellRendering(data: FormData): any {
        return (currentRow: number, currentCol: number) => {
            const cellMeta: Handsontable.CellMeta = {};
            if (currentCol === 0) {
                cellMeta.className = 'htMiddle htCenter';
                cellMeta.readOnly = true;
            }
            if (currentCol === 4) {
                cellMeta.readOnly = true;
                cellMeta.validator = (_value, callback) => callback(true);
                return cellMeta;
            }
            const rows = data.rows ? data.rows : [];

            const row = rows.find((rowField, index) => index === currentRow);
            if (!row) {
                return cellMeta;
            }
            if (this.negotiationFieldAccess && !this.negotiationFieldAccess(row.field)) {
                cellMeta.readOnly = true;
                cellMeta.validator = (_value, callback) => callback(true);
                return cellMeta;
            }
            if (row.cells && row.cells.value && row.cells.value.dataType && row.cells.value.dataType.code === CONTRACT_CODE_TYPE ) {
              cellMeta.readOnly = true;
            }
            if (currentCol !== 2) {
                if (currentCol === 1) {
                    if (row.children && row.duplicate) {
                        cellMeta.renderer = this.renderControllerService.renderNameGroup(data, this);
                    }
                    const cellValue = row.cells && row.cells.value ? row.cells.value : null;
                    const cellName = row.cells && row.cells.name ? row.cells.name : null;
                    if (cellValue && cellValue.dataType && cellValue.dataType.code === DATA_TYPE_SELECT && cellValue.note) {
                        cellMeta.comment = {
                            value: cellValue.note,
                            readOnly: true
                        };
                    }
                    if (cellName.className) {
                        cellMeta.className = cellName.className;
                    }
                }
                if (currentCol === 3) {
                    cellMeta.className = `note`;
                }
                return cellMeta;
            }
            if (row.children) {
                return cellMeta;
            }
            const rowCache = JSON.parse(JSON.stringify(row));
            const cellValue = rowCache.cells && rowCache.cells.value ? rowCache.cells.value : null;
            let cellTypeCode = cellValue && cellValue.type && cellValue.type.code ? cellValue.type.code : null;
            if (cellValue.fixedFunction && cellValue.fixedFunction.code === FIXED_FUNCTION_SUM) {
                cellTypeCode = HandsontableFieldTypeContrants.number;
            }
            switch (cellTypeCode) {
                case HandsontableFieldTypeContrants.checkbox:
                    cellMeta.renderer = this.renderControllerService.renderCheckboxController(cellValue);
                    cellMeta.editor = false;
                    break;
                case HandsontableFieldTypeContrants.radio:
                    cellMeta.renderer = this.renderControllerService.renderRadioController(cellValue);
                    cellMeta.editor = false;
                    break;
                case HandsontableFieldTypeContrants.combobox:
                    cellMeta.type = HandsontableFieldTypeContrants.autocomplete;
                    cellMeta.strict = true;
                    cellMeta.filter = false;
                    cellMeta.allowInvalid = false;
                    cellMeta.allowEmpty = this.hasCustomerEditor(row);
                    break;
                case HandsontableFieldTypeContrants.date:
                    cellMeta.type = HandsontableFieldTypeContrants.date;
                    cellMeta.className += ' date htRight';
                    cellMeta.editor = HandsontableFieldTypeContrants.date;
                    break;
                case HandsontableFieldTypeContrants.moneyFormat:
                    cellMeta.type = HandsontableFieldTypeContrants.numeric;
                    cellMeta.renderer = this.renderControllerService.renderMoneyFormat(cellValue);
                    cellMeta.numericFormat = {
                        pattern: '0.00',
                        culture: 'en-US' // this is the default culture, set up for USD
                    }
                    break;
                case HandsontableFieldTypeContrants.numberPercent:
                    cellMeta.type = HandsontableFieldTypeContrants.numeric;
                    cellMeta.renderer = this.renderControllerService.renderPercent(cellValue);
                    cellMeta.numericFormat = {
                        pattern: '0.00',
                        culture: 'en-US' // this is the default culture, set up for USD
                    }
                    break;
                case HandsontableFieldTypeContrants.number:
                    cellMeta.type = HandsontableFieldTypeContrants.numeric;
                    cellMeta.renderer = this.renderControllerService.renderNumber(cellValue);
                    cellMeta.numericFormat = {
                        pattern: '0.00',
                        culture: 'en-US' // this is the default culture, set up for USD
                    }
                    break;
                case HandsontableFieldTypeContrants.phone:
                    cellMeta.className += ` htRight`;
                    cellMeta.type = HandsontableFieldTypeContrants.text;
                    break;
                case HandsontableFieldTypeContrants.email:
                case HandsontableFieldTypeContrants.taxCode:
                case HandsontableFieldTypeContrants.textbox:
                    cellMeta.type = HandsontableFieldTypeContrants.text;
                    break;
                default: break;
            }

            if (cellValue.data) {
                cellMeta.source = cellValue.data.map(data => {
                    return data.value
                });
            }
            if (cellValue.filterData) {
                cellMeta.source = cellValue.filterData.map(data => {
                    return data.value
                });
            }
            if (cellValue.note && cellValue.dataType && cellValue.dataType.code !== DATA_TYPE_SELECT) {
                cellMeta.placeholder = cellValue.note;
            }

            if (cellTypeCode !== HandsontableFieldTypeContrants.combobox) {
                cellMeta.validator = this.validationFunc(cellValue, row, currentRow);
            }
            return cellMeta;
        }
    }

    public hasRequiredValue(row: any, value: any): boolean {
        if (value === null || value === undefined ||
            (typeof value === 'string' && value.trim() === '')) {
            return false;
        }
        const cell = row && row.cells && row.cells.value;
        if (cell && cell.type && cell.type.code === HandsontableFieldTypeContrants.checkbox) {
            try {
                const selected = typeof value === 'string' ? JSON.parse(value) : value;
                return Array.isArray(selected) && selected.length > 0;
            } catch (_) {
                return false;
            }
        }
        return !Array.isArray(value) || value.length > 0;
    }

    public isRequiredField(row: any): boolean {
        const cell = row && row.cells && row.cells.value;
        if (this.negotiationFieldAccess) {
            const config = row && row.field && row.field.fieldConfig;
            const required = config && Array.isArray(config.validations) &&
                config.validations.find(rule => rule.code === 'REQUIRED');
            const value = required ? required.validationValue : cell && cell.validation && cell.validation.required;
            return value === true || value === 'true';
        }
        return !!(cell && cell.validation && cell.validation.required);
    }

    private hasCustomerEditor(row: any): boolean {
        // Assigned customers must complete their own required fields in the popup.
        if (this.negotiationFieldAccess) {
            return false;
        }
        if (row && row.cells && row.cells.value && row.cells.value.missingRequired) {
            return false;
        }
        const field = row && row.field;
        const config = field && field.fieldConfig;
        return !!(field && field.customerId && config &&
            (config.editorSignatureId != null || (config.editorSignatureIdPresent && field.signatureId != null)));
    }

    private validationFunc(cell: any, rowData: any, row: number) {
        return (value: any, callBack: any) => {
            const instance = this.hotRegister.getInstance('negotiation-template');
            let errorMess = '';
            const hasValue = this.hasRequiredValue(rowData, value);
            if (!hasValue && this.isRequiredField(rowData) && !this.hasCustomerEditor(rowData)) {
                errorMess = this.message.getMessage("handsontable.error.required");
            }

            if (hasValue) {
                //Check content's maxlength & minlength
                if (value && cell.validation && cell.validation.minLength && value.toString().length < cell.validation.minLength) {
                    if (cell.validation.maxLength) {
                        errorMess = this.message.getMessage("handsontable.error.minMaxlength") + cell.validation.minLength + this.message.getMessage("handsontable.error.toLabel") +
                            cell.validation.maxLength + this.message.getMessage("handsontable.error.characterLabel");
                    } else {
                        errorMess = this.message.getMessage("handsontable.error.minlength") +
                            cell.validation.minLength + this.message.getMessage("handsontable.error.characterLabel");
                    }
                } else if (value && cell.validation && cell.validation.maxLength && value.toString().length > cell.validation.maxLength) {
                    if (cell.validation.minLength) {
                        errorMess = this.message.getMessage("handsontable.error.minMaxlength") + cell.validation.minLength + this.message.getMessage("handsontable.error.toLabel") +
                            cell.validation.maxLength + this.message.getMessage("handsontable.error.characterLabel");
                    } else {
                        errorMess = this.message.getMessage("handsontable.error.maxlength") +
                            cell.validation.maxLength + this.message.getMessage("handsontable.error.characterLabel");
                    }
                } else if (value && cell.type && cell.type.code === HandsontableFieldTypeContrants.textbox && (!cell.validation || (cell.validation && !cell.validation.maxLength)) && value.toString().length > 4000) {
                    if (cell.validation && cell.validation.minLength) {
                        errorMess = this.message.getMessage("handsontable.error.minMaxlength") + cell.validation.minLength + this.message.getMessage("handsontable.error.toLabel") +
                            '4000' + this.message.getMessage("handsontable.error.characterLabel");
                    } else {
                        errorMess = this.message.getMessage("handsontable.error.maxlength") +
                            '4000' + this.message.getMessage("handsontable.error.characterLabel");
                    }
                }

                //Check content's format
                const cellTypeCode = cell.type && cell.type.code ? cell.type.code : null;
                switch (cellTypeCode) {
                    case HandsontableFieldTypeContrants.phone:
                        const phoneRegex = new RegExp(Common.PHONE_PATTERN);
                        if (!phoneRegex.test(value)) {
                            errorMess += " " + this.message.getMessage("handsontable.error.format_phone");
                        }
                        break;
                    case HandsontableFieldTypeContrants.email:
                        const emailRegex = new RegExp(Common.EMAIL_PATTERN);
                        if (!emailRegex.test(value)) {
                            errorMess += " " + this.message.getMessage("handsontable.error.format_email");
                        }
                        break;
                    case HandsontableFieldTypeContrants.date:
                        const dateRegex = new RegExp(Common.DATE_PATTERN);
                        if (!dateRegex.test(value)) {
                            errorMess += " " + this.message.getMessage("handsontable.error.format_date");
                        }
                        break;
                    case HandsontableFieldTypeContrants.taxCode:
                        // if (!this.validateTaxcode(value)) {
                        //     errorMess += " " + this.message.getMessage("handsontable.error.format_tax_code");
                        // }
                        break;
                    case HandsontableFieldTypeContrants.number:
                    case HandsontableFieldTypeContrants.numberPercent:
                        const numberRegex = new RegExp(Common.NUMBER_PATTERN);
                        if (!numberRegex.test(value)) {
                            errorMess += " " + this.message.getMessage("handsontable.error.format_number");
                        }
                        break;
                    case HandsontableFieldTypeContrants.moneyFormat:
                        const moneyRegex = new RegExp(Common.NUMBER_PATTERN);
                        if (!moneyRegex.test(value)) {
                            errorMess += " " + this.message.getMessage("handsontable.error.format_money");
                        }
                        break;
                    default: break;
                }
            }

            if (errorMess) {
                instance.setDataAtCell(row, 3, errorMess);
                callBack(false);
            } else {
                if (instance.getDataAtCell(row, 3)) {
                    instance.setDataAtCell(row, 3, '');
                }
                callBack(true);
            }
        }
    }

    private afterChange(data: FormData) {
        return (changes: any, source: string) => {
            if (!changes) {
                return;
            }
            if (source === 'UndoRedo.undo') {
                return;
            }
            const rows = data.rows ? data.rows : [];
            const ErrorFormula = ['#DIV/0!', '#ERROR!', '#VALUE!', '#REF!', '#NAME?', '#N/A', '#NUM!'];
            const dataChange = {};
            // cho version handsontable 8
            const tableInstance = this.hotRegister.getInstance('negotiation-template');
            const dataCells = tableInstance.getDataAtCol(2);
            dataCells.forEach((dataCell, index) => {
                const row = rows[index] ? rows[index] : null;
                if (row && row.cells && row.cells.value && row.cells.value.formula && (row.cells.value.value !== dataCell || source === 'insert')) {
                    if (!ErrorFormula.includes(dataCell)) {
                        row.cells.value.value = dataCell;
                        dataChange[row.cells.value.code] = dataCell;
                    } else {
                        row.cells.value.value = null;
                        dataChange[row.cells.value.code] = dataCell;
                    }
                }
                if (changes.length && rows.length) {
                    const change = changes.find(item => item[0] === index && item[1] === 'value');
                    if (change) {
                        const indexRow = change[0];
                        const colName = change[1];
                        const oldValue = change[2] === null ? '' : change[2];
                        const newValue = change[3] === null ? '' : change[3];

                        // Restored combobox labels are already in the grid when 'first' runs.
                        // Refresh their references even when the displayed label has not changed.
                        if ((oldValue === newValue && source !== 'insert' && source !== 'first') || ErrorFormula.includes(newValue)) {
                            return;
                        }

                        this.dirty.next(true);

                        const rowChange = rows[indexRow];
                        const cell = rowChange && rowChange.cells && rowChange.cells[colName] ? rowChange.cells[colName] : null;
                        if (cell && newValue && typeof newValue === 'string' && newValue.substring(0, 1) === '=') {
                            cell.formula = this.parseFormulaTyping(newValue, rows);
                            cell.value = dataCells[indexRow];
                        } else if (cell) {
                            cell.value = newValue;
                            if (cell.formula) {
                                cell.formula = null;
                            }
                        }
                        if (cell && cell.type && cell.type.code === FIELD_TYPE_COMBOBOX) {
                            const selected = cell.data.find(option => option.value === newValue);
                            dataChange[cell.code] = selected ? selected : {};
                        } else if (cell) {
                            if (newValue && typeof newValue === 'string' && newValue.substring(0, 1) === '=') {
                                dataChange[cell.code] = dataCells[indexRow];
                            } else {
                                dataChange[cell.code] = newValue;
                            }
                        }
                    }
                }
            })
            // cho TH khi update version handsontable to 9
            // if (changes.length && rows.length) {

            // for (const change of changes) {
            //     const indexRow = change[0];
            //     const colName = change[1];
            //     const oldValue = change[2];
            //     const newValue = change[3];

            //     if (oldValue === newValue || colName === 'name' || colName === 'note' || colName === 'orderNo' || ErrorFormula.includes(newValue)) {
            //         return;
            //     }
            //     this.dirty.next(true);

            //     const rowChange = rows[indexRow];
            //     const cell = rowChange.cells[colName] ? rowChange.cells[colName] : null;
            //     if (cell) {
            //         cell.value = newValue;
            //     } else {
            //         continue;
            //     }
            //     if (cell.type && cell.type.code === FIELD_TYPE_COMBOBOX) {
            //         const selected = cell.data.find(option => option.value === newValue);
            //         dataChange[cell.code] = selected ? selected : {};
            //     } else {
            //         dataChange[cell.code] = newValue;
            //     }
            // }
            if (Object.keys(dataChange).length) {
                const relatedFieldDataRow = [];
                for (const [index, row] of rows.entries()) {
                    if (row.children) {
                        continue;
                    }
                    const cellValue = row.cells && row.cells.value ? row.cells.value : null;
                    // tham chieu khach hang
                    if (cellValue.relatedFieldCode && dataChange[cellValue.relatedFieldCode] && cellValue.dataType && cellValue.dataType.code === 'tham_chieu_ben_khach_hang') {
                        const objectData = dataChange[cellValue.relatedFieldCode];
                        const refCategoryFieldCode = cellValue.refCategoryField && cellValue.refCategoryField.code ? cellValue.refCategoryField.code : null;
                        if (refCategoryFieldCode && objectData[refCategoryFieldCode] !== null) {
                            cellValue.value = objectData[refCategoryFieldCode];
                            if (cellValue.value !== dataCells[index]) {
                                relatedFieldDataRow.push([index, 2, cellValue.value]);
                            }
                        } else {
                            cellValue.value = null;
                            if (cellValue.value != dataCells[index]) {
                                relatedFieldDataRow.push([index, 2, cellValue.value]);
                            }
                        }
                        continue;
                    }
                    // tham chieu danh muc
                    if (cellValue.relatedFieldCode && dataChange[cellValue.relatedFieldCode] && cellValue.dataType && cellValue.dataType.code === 'ref') {
                        if (source === 'first' && !cellValue.valueId) {
                            continue;
                        }
                        const objectData = dataChange[cellValue.relatedFieldCode];
                        cellValue.filterData = cellValue.data.filter(item => {
                            return item.parent_code === objectData.code
                        });
                        if (cellValue.filterData.length) {
                            if (cellValue.type && cellValue.type.code === HandsontableFieldTypeContrants.number || cellValue.type.code === HandsontableFieldTypeContrants.moneyFormat) {
                                const lang = localStorage.getItem('__language');
                                const number = lang == 'vi' ? cellValue.filterData[0].value.replace(/\./g, '') : cellValue.filterData[0].value.replace(/\,/g, '');
                                const found = lang == 'vi' ? number.match(/\,/g) : number.match(/\./g);
                                if (found && found.length > 1) {
                                    cellValue.value = cellValue.filterData[0].value;
                                    if (cellValue.value != dataCells[index]) {
                                        relatedFieldDataRow.push([index, 2, cellValue.value]);
                                    }
                                } else {
                                    cellValue.value = lang == 'vi' ? number.replace(',', '.') : number;
                                    if (cellValue.value != dataCells[index]) {
                                        relatedFieldDataRow.push([index, 2, cellValue.value]);
                                    }
                                }
                            } else {
                                cellValue.value = cellValue.filterData[0].value;
                                if (cellValue.value != dataCells[index]) {
                                    relatedFieldDataRow.push([index, 2, cellValue.value]);
                                }
                            }
                        } else {
                            cellValue.value = null;
                            if (cellValue.value != dataCells[index]) {
                                relatedFieldDataRow.push([index, 2, cellValue.value]);
                            }
                        }
                        continue;
                    }
                    // truong co dinh
                    if (cellValue.fixedFunction && cellValue.fixedFunction.code === FIXED_FUNCTION_SUM) {
                        const field = rows.filter(row => {
                            return row.cells && row.cells.value && row.cells.value.value !== null && row.cells.value.value !== '' && row.cells.value.code.includes(cellValue.fixedFieldCode);
                        });
                        if (field.length) {
                            let sumResult = 0;
                            field.forEach(item => {
                                let number = item.cells.value.value;
                                if (isNaN(item.cells.value.value)) {
                                    number = '0';
                                }
                                sumResult += Number(number);
                            });
                            if (!isNaN(sumResult) && cellValue.value !== sumResult.toString()) {
                                cellValue.value = sumResult.toString();
                                if (cellValue.value !== dataCells[index]) {
                                    relatedFieldDataRow.push([index, 2, cellValue.value]);
                                }
                            }
                        } else {
                            if (cellValue.value) {
                                cellValue.value = null;
                                if (cellValue.value != dataCells[index]) {
                                    relatedFieldDataRow.push([index, 2, cellValue.value]);
                                }
                            }
                        }
                        continue;
                    } else if (cellValue.fixedFieldCode && Object.keys(dataChange).includes(cellValue.fixedFieldCode)) {
                        if (cellValue.fixedFunction && cellValue.fixedFunction.code === FIXED_FUNCTION_MONEY_TO_TEXT) {
                            let text = cellValue.defaultValue ? cellValue.defaultValue : null;
                            let numberText = '0';
                            if (dataChange[cellValue.fixedFieldCode] && ErrorFormula.includes(dataChange[cellValue.fixedFieldCode])) {
                                numberText = '';
                            } else if (dataChange[cellValue.fixedFieldCode]) {
                                numberText = dataChange[cellValue.fixedFieldCode].toString();
                            } else if (dataChange[cellValue.fixedFieldCode] === null || dataChange[cellValue.fixedFieldCode] === '') {
                                numberText = '';
                            }
                            cellValue.value = this.convertCurrencyService.convertToVietnamText(numberText, text);
                            if (cellValue.value != dataCells[index]) {
                                relatedFieldDataRow.push([index, 2, cellValue.value]);
                            }
                        } else if (cellValue.fixedFunction && cellValue.fixedFunction.code === FIXED_FUNCTION_NUMBER_TO_TEXT) {
                            let numberText = '0';
                            if (dataChange[cellValue.fixedFieldCode] && ErrorFormula.includes(dataChange[cellValue.fixedFieldCode])) {
                                numberText = ''
                            } else if (dataChange[cellValue.fixedFieldCode]) {
                                numberText = dataChange[cellValue.fixedFieldCode].toString();
                            } else if (dataChange[cellValue.fixedFieldCode] === null || dataChange[cellValue.fixedFieldCode] === '') {
                                numberText = '';
                            }
                            cellValue.value = this.convertCurrencyService.convertToVietnamText(numberText);
                            if (cellValue.value != dataCells[index]) {
                                relatedFieldDataRow.push([index, 2, cellValue.value]);
                            }
                        }
                        continue;
                    }

                    // if (cellValue.fixedMoneyCode && Object.keys(dataChange).includes(cellValue.fixedMoneyCode)) {
                    //     const money = rows.find(item => item.cells && item.cells.value && item.cells.value.code === cellValue.fixedFieldCode);
                    //     if (money.cells.value.value) {
                    //         relatedFieldDataRow.push([index, 2, this.convertCurrencyService.convertToVietnamText(money.cells.value.value ? money.cells.value.value.toString() : '', dataChange[cellValue.fixedMoneyCode] ? dataChange[cellValue.fixedMoneyCode].description : '')]);
                    //     }
                    //     continue;
                    // }

                }
                if (relatedFieldDataRow.length) {
                    tableInstance.setDataAtCell(relatedFieldDataRow, 'change');
                }
            }
            // }

        }
    }

    beforeChange(data: FormData) {
        return (changes: any, source: string) => {
            console.log(changes, source);

            if (!changes) {
                return;
            }

            if (source === 'change' || source === 'first') {
                return;
            }

            const rows = data.rows ? data.rows : [];
            const ErrorFormula = ['#DIV/0!', '#ERROR!', '#VALUE!', '#REF!', '#NAME?', '#N/A', '#NUM!'];
            if (this.negotiationFieldAccess && source !== 'loadData' &&
                changes.some(change => change && (change[1] === 'value' || change[1] === 2) &&
                    !this.negotiationFieldAccess(rows[change[0]] && rows[change[0]].field))) {
                return false;
            }
            if (changes.length && rows.length) {
                const dataRow = [];
                let isChange = false;
                for (const change of changes) {
                    const indexRow = change[0];
                    const colName = change[1];
                    const oldValue = change[2];
                    const newValue = typeof change[3] === 'string' ? change[3].trim()
                        : typeof change[3] === 'number' ? String(change[3]) : '';

                    if (oldValue === newValue || colName === 'name' || colName === 'note' || colName === 'orderNo') {
                        continue;
                    }
                    const rowChange = rows[indexRow];
                    const cell = rowChange && rowChange.cells && rowChange.cells[colName] ? rowChange.cells[colName] : null;

                    if (!cell) {
                        continue;
                    }

                    if (cell.type && cell.type.code === HandsontableFieldTypeContrants.date) {
                        const dateRegex = new RegExp(Common.DATE_PATTERN);
                        if (!dateRegex.test(newValue) && change[3] !== null) {
                            dataRow.push([indexRow, 2, this.proccessDate(newValue)]);
                            isChange = true;
                        } else {
                            dataRow.push([indexRow, 2, newValue]);
                        }
                    } else if (cell.type && cell.type.code === HandsontableFieldTypeContrants.numberPercent && newValue.substring(newValue.length - 1, newValue.length) === '%') {
                        const numberRegex = new RegExp(Common.NUMBER_PATTERN);
                        const newValuePercent = newValue.substring(0, newValue.length - 1).trim();
                        if (numberRegex.test(newValuePercent)) {
                            dataRow.push([indexRow, 2, newValuePercent]);
                            isChange = true;
                        } else if (newValue) {
                            dataRow.push([indexRow, 2, newValue]);
                        }
                        // } else if (ErrorFormula.includes(newValue) && oldValue.substring(0,1) === '=') {
                        //     dataRow.push([indexRow, 2, oldValue]);
                        //     isChange = true;
                        // } else if (!ErrorFormula.includes(newValue)){
                    } else if (cell.type && (cell.type.code === HandsontableFieldTypeContrants.number || cell.type.code === HandsontableFieldTypeContrants.numberPercent || cell.type.code === HandsontableFieldTypeContrants.moneyFormat)) {
                        const numberRegex = new RegExp(Common.NUMBER_PATTERN);
                        if (numberRegex.test(newValue)) {
                            if (Number(newValue) >= 999999999999999) {
                                const numberRemoveDot = newValue.replace(/\./g, '').replace(/\,/g, '');
                                const numberNew = numberRemoveDot.substring(0, 15) + '.' + numberRemoveDot.substring(15, 17);
                                dataRow.push([indexRow, 2, Number(newValue) >= 0 ? numberNew : '-' + numberNew]);
                                isChange = true;
                            } else {
                                const newNumber = newValue.replace(/\,/g, '.');
                                const numberResult = +(Math.round(Number(newNumber.replace('-', '') + 'e+2')) + 'e-2');
                                dataRow.push([indexRow, 2, Number(newNumber) >= 0 ? numberResult : '-' + numberResult]);
                                isChange = true;
                            }
                        } else if (newValue) {
                            dataRow.push([indexRow, 2, newValue]);
                        } else {
                            dataRow.push([indexRow, 2, '']);
                            isChange = true;
                        }
                    } else if (cell.type && cell.type.code !== HandsontableFieldTypeContrants.number && cell.type.code !== HandsontableFieldTypeContrants.moneyFormat && cell.type.code !== HandsontableFieldTypeContrants.numberPercent && newValue.length > 4000) {
                        dataRow.push([indexRow, 2, newValue.substring(0, 4000)]);
                        isChange = true;
                    } else {
                        dataRow.push([indexRow, 2, newValue]);
                    }
                }

                if (isChange) {
                    const tableInstance = this.hotRegister.getInstance('negotiation-template');
                    tableInstance.setDataAtCell(dataRow, 'change');
                    return false;
                }
            }

        }
    }

    beforePaste(dataForm: FormData) {
        return (data, coords) => {
            if (coords.length) {
                const dataSave = [];
                let isCancel = false;
                const rows = dataForm.rows ? dataForm.rows : [];
                const amountRow = coords[0].endRow - coords[0].startRow;
                const amountCol = coords[0].endCol - coords[0].startCol;
                const arrIndexRow = amountRow ? Array.from({ length: amountRow }, (v, k) => { return k + coords[0].startRow }) : [coords[0].endRow];
                const arrIndexCol = amountCol ? Array.from({ length: amountCol }, (v, k) => { return k + coords[0].startCol }) : [coords[0].endCol];
                const indexColValue = arrIndexCol.findIndex(item => item === 1);
                if (indexColValue >= 0) {
                    for (const [index, indexForm] of arrIndexRow.entries()) {
                        const cellValue = rows[indexForm] && rows[indexForm].cells && rows[indexForm].cells.value ? rows[indexForm].cells.value : {};
                        if (cellValue.type && (cellValue.type.code === HandsontableFieldTypeContrants.number || cellValue.type.code === HandsontableFieldTypeContrants.moneyFormat)) {
                            const lang = localStorage.getItem('__language');
                            data[index][indexColValue] = lang === 'vi' ? data[index][indexColValue].replace(/\./g, '').replace(/\,/g, '.') : data[index][indexColValue].replace(/\,/g, '');
                            dataSave.push([indexForm, 2, data[index][indexColValue]]);
                            isCancel = true;
                        } else {
                            dataSave.push([indexForm, 2, data[index][indexColValue]]);
                        }
                    }
                }

                if (isCancel) {
                    const instance = this.hotRegister.getInstance('negotiation-template');
                    instance.setDataAtCell(dataSave, 'change');
                    return false;
                }
            }
        }
    }

    proccessDate(value: string): string {
        const now = new Date();
        value = value ? value.toString() : '';
        let day = value.substring(0, 2);
        let month = value.substring(2, 4);
        let year = value.substring(4, 8);
        const monthNow =
            now.getMonth() > 8
                ? (now.getMonth() + 1).toString()
                : '0' + (now.getMonth() + 1);
        const yearNow = now.getFullYear().toString();
        const daysInMonth = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            0
        ).getDate();
        if (!value) {
            day =
                now.getDate() > 9
                    ? now.getDate().toString()
                    : '0' + now.getDate().toString();
            month = monthNow;
            year = yearNow;
        } else if (value.length === 1) {
            day = '0' + value;
            month = monthNow;
            year = yearNow;
        } else if (value.length === 2 && Number(day) <= daysInMonth) {
            month = monthNow;
            year = yearNow;
        } else if (value.length === 2 && Number(day) > daysInMonth) {
            day = '0' + value.substring(0, 1);
            month = '0' + value.substring(1, 2);
            year = yearNow;
        } else if (value.length === 3 && Number(day) <= daysInMonth) {
            month = '0' + value.substring(2, 3);
            year = yearNow;
        } else if (value.length === 3 && Number(day) > daysInMonth) {
            day = '0' + value.substring(0, 1);
            month = value.substring(1, 3);
            if (Number(month) > 12) {
                return value;
            }
            year = yearNow;
        } else if (
            value.length === 4 &&
            Number(day) <= daysInMonth &&
            Number(month) <= 12
        ) {
            year = yearNow;
        }
        if (
            Number(day).toString() === 'NaN' ||
            Number(month).toString() === 'NaN' ||
            Number(year).toString() === 'NaN' ||
            Number(day) === 0 ||
            Number(month) === 0 ||
            Number(year) === 0
        ) {
            return value;
        }
        const date = `${day}/${month}/${year}`;
        return date;
    }

    /**
   * (1)	Kiểm tra cấu trúc mã 10 số:
   * Số A = 31*số đầu tiên bên trái
   * + 29*số thứ 2 + 23*số thứ 3
   * + 19*số thứ 4 + 17*số thứ 5
   *  + 13*số thứ 6 + 7* số thứ 7
   *  + 5*số thứ 8+3*số thứ 9.
   * Lấy số A chia cho 11 lấy phần dư là số B,
   *  lấy 10 trừ số B bằng số thứ 10 trong mã số thuế là đúng,
   *  ngược lại là sai.
   * (2)	Kiểm tra cấu trúc mã 13 số: bao gồm việc kiểm tra cấu trúc mã 10 số và kiểm tra giá trị của 3 số cuối.
   * Ba số cuối phải được nhập đầy đủ.
   * @param taxCode The tax code.
   */
    public validateTaxcode(taxCode: string) {
        if (!taxCode) {
            return false;
        }

        var tempTaxCode = taxCode.trim();

        if (tempTaxCode.length < 10 || tempTaxCode.length > 14) {
            return false;
        }

        // - must start from 10 - 13.
        if (tempTaxCode.indexOf("-") < 10 && tempTaxCode.indexOf("-") >= 0) {
            return false;
        }

        // replace - by "".
        tempTaxCode = tempTaxCode.replace("-", "");

        var firstCode = tempTaxCode.substring(0, 10).trim();
        if (firstCode.length < 10) {
            return false;
        }

        var secondCode = "";
        if (tempTaxCode.length > 10) {
            secondCode = tempTaxCode.substring(10).trim();
        }

        // string to array of numbers.
        var firstCodeArr = tempTaxCode.split('');

        var a =
            // 31*số đầu tiên bên trái
            Number(firstCodeArr[0]) * 31
            // 29*số thứ 2
            + Number(firstCodeArr[1]) * 29
            // 23*số thứ 3
            + Number(firstCodeArr[2]) * 23
            // 19*số thứ 4
            + Number(firstCodeArr[3]) * 19
            // 17*số thứ 5
            + Number(firstCodeArr[4]) * 17
            // 13*số thứ 6
            + Number(firstCodeArr[5]) * 13
            // 7* số thứ 7
            + Number(firstCodeArr[6]) * 7
            // 5*số thứ 8
            + Number(firstCodeArr[7]) * 5
            // 3*số thứ 9.
            + Number(firstCodeArr[8]) * 3;

        // Lấy số A chia cho 11 lấy phần dư là số B,
        var b = a % 11;
        if (10 - b !== Number(firstCodeArr[9])) {
            return false;
        }

        // not exist second code.
        if (secondCode.length === 0 && taxCode.indexOf("-") <= 0) return true;
        if (secondCode.length === 3 && taxCode.indexOf("-") > 0) {
            var secondCodeArr = secondCode.split('');
            a = Number(secondCodeArr[0]) + Number(secondCodeArr[1]) + Number(secondCodeArr[2]);
            if (isNaN(a) || isUndefined(a)) {
                return false;
            }
        } else {
            return false;
        }

        return true;
    }

    public parseIndexFormula(
        formula: string,
        data: any,
        isChild: boolean
    ): string {
        const regex = /<[*A-Z0-9_]{2,}>/g;
        const listCode = formula.match(regex);
        if (listCode && listCode.length) {
            listCode.forEach(code => {
                // Selected code remove arrow
                let removeArrowCode;
                if (code.substring(1, 2) === '*' && code.substring(2, 3) !== '*') {
                    removeArrowCode = code.substring(2, code.length - 1);
                } else if (code.substring(1, 3) === '**') {
                    removeArrowCode = code.substring(3, code.length - 1);
                } else {
                    removeArrowCode = code.substring(1, code.length - 1);
                }

                // Selected col index
                let selectedColIndex: number;
                let selectedRowIndex: number;
                // Selected row index
                if (!isChild || !removeArrowCode.includes('GROUP')) {
                    selectedRowIndex =
                        data.rows.findIndex((row: any) => {
                            for (const property in row.cells) {
                                if (
                                    !row.cells ||
                                    !row.cells[property] ||
                                    !row.cells[property].code
                                ) {
                                    continue;
                                }
                                if (row.cells[property].code === removeArrowCode) {
                                    selectedColIndex = this.getSelectedColIndex(
                                        data.colHeaders,
                                        property
                                    );
                                    return true;
                                }
                            }
                            return false;
                        }) + 1;
                } else {
                    for (const [index, row] of data.rows.entries()) {
                        // if (row.children && row.children.length && index > indexField) {
                        //   break;
                        // }
                        for (const property in row.cells) {
                            if (
                                !row.cells ||
                                !row.cells[property] ||
                                !row.cells[property].code
                            ) {
                                continue;
                            }
                            if (row.cells[property].code === removeArrowCode) {
                                selectedColIndex = this.getSelectedColIndex(
                                    data.colHeaders,
                                    property
                                );
                                selectedRowIndex = index + 1;
                            }
                        }
                    }
                }
                // Replace formula
                if (selectedRowIndex) {
                    formula = formula.replace(
                        code,
                        `${this.convertNumberToText(
                            code.substring(1, 2) === '*' && code.substring(2, 3) !== '*'
                                ? selectedColIndex - 2
                                : (code.substring(1, 3) === '**' ? selectedColIndex - 1 : selectedColIndex)
                        )}${selectedRowIndex}`
                    );
                } else {
                    formula = formula.replace(code, removeArrowCode);
                }
            });
        }
        return formula;
    }

    public getSelectedColIndex(colHeaders: string[], property: string): number {
        const headerField = COLUMN_FIELD.find(
            field => field.fieldName === property
        );
        const headerName = headerField ? headerField.header : null;
        return colHeaders.indexOf(headerName);
    }
    public convertNumberToText(number: number): string {
        // fix sonar
        const isNaN =  Number.isNaN(number);
        if (isNaN || number === null) {
            return '';
        }
        return String.fromCharCode(65 + number);
    }

    // fix sonar, ham khong su dung

    // public parseSumFunction(cell: any, data: FormData) {
    //     const indexSumCell = data.rows.findIndex(item => item.cells && item.cells.value && item.cells.value.code === cell.code);
    //     const indexFieldCell = data.rows.findIndex(item => item.cells && item.cells.value && item.cells.value.code === cell.fixedFieldCode);
    //     const fieldDataRow = data.rows.find(row => row.cells && row.cells.value && row.cells.value.code === cell.fixedFieldCode);
    //     const fieldDataName = fieldDataRow && fieldDataRow.cells && fieldDataRow.cells.name && fieldDataRow.cells.name.value ? fieldDataRow.cells.name.value : '';
    //     if (indexFieldCell > indexFieldCell) {
    //         return `=SUMIF(B${indexSumCell + 2}:B${data.rows.length};"${fieldDataName}";C${indexSumCell + 2}:C${data.rows.length})`;
    //     } else {
    //         return `=SUMIF(B${indexFieldCell}:B${indexSumCell};"${fieldDataName}";C${indexFieldCell}:C${indexSumCell})`;
    //     }
    // }

    public parseFormulaTyping(formula: string, rows: any) {
        // loai bo chuoi
        const checkText = `\"[\d\wÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂẾưăạảấầẩẫậắằẳẵặẹẻẽềềểếỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵỷỹ\s\!\#\$\%\&\'\(\)\*\+\,\\\.\/\:\;\<\=\>\?\@\[\]\^\_\`\{\|\}\~\…\-\”\“]*\"`;
        const regexString = new RegExp(checkText, "g");
        const listString = formula.match(regexString);
        if (listString && listString.length) {
            listString.forEach((item) => {
                formula = formula.replace(item, "<*>");
            });
        }
        // thay vi tri o bang code truong
        const regex = /([ABC]{1}[\d]{1,4})/g;
        const listCol = formula.match(regex);
        if (!listCol || !listCol.length) {
            if (listString && listString.length) {
                listString.forEach((item) => {
                    formula = formula.replace("<*>", item);
                });
            }
            return formula;
        }
        listCol.forEach((col) => {
            let colPosition;
            if (col.substring(0, 1) === "A") {
                colPosition = col.replace("A", "C");
            } else if (col.substring(0, 1) === "B") {
                colPosition = col.replace("B", "C");
            } else {
                colPosition = col;
            }
            const row = rows[Number(colPosition.replace('C', '')) - 1];
            const cellValue = row && row.cells && row.cells.value ? row.cells.value : null;
            if (cellValue) {
                formula = formula.replace(
                    col,
                    "<" +
                    (col.substring(0, 1) === "A" ? "*" : (col.substring(0, 1) === "B" ? "**" : "")) +
                    cellValue.code +
                    ">"
                );
            }
        });
        if (listString && listString.length) {
            listString.forEach((item) => {
                formula = formula.replace("<*>", item);
            });
        }
        return formula;
    }
}
